import { TIntegration } from '@/core/modules/integrations/integrations'
import { AssetsClient } from '@core/api/assets-client'
import { ProductivityIntegrationsClient } from '@core/api/productivity-integration-client'
import { BaseService } from '@core/base/service/base-service'
import { sleep } from '@core/utils/time'
import {
    MicrosoftIntegrationData,
    GoogleIntegrationData,
    CrowdstrikeIntegrationData,
    IntegrationRequestResponseData,
    IntegrationsRequestsResponse,
    IntegrationsRequestsResponseData,
    IntegrationRequestStatusEnum,
    JobTypeEnum,
    SecurityServiceStateEnum,
} from '@atbay/productivity-integrations-node-sdk'
import { AxiosError } from 'axios'
import { TIntegrationProviders } from '@core/store/Integrations/integrations.store'
import { transformProviderObject } from '@/core/services/utils/utils'

export const IntegrationsService =
    new (class IntegrationsService extends BaseService {
        private readonly assetsClient = AssetsClient
        private readonly client = ProductivityIntegrationsClient
        private INTERVAL = import.meta.env.DEV ? 1000 : 1000 * 5
        private GET_INTEGRATION_MAX_RETRIES = 10

        constructor() {
            super('integrations.service')
        }

        async getIntegrations() {
            const [error, result] = await this.request(
                () =>
                    this.http.get(
                        `/api/accounts/${this.accountId}/integrations`
                    ) // TODO: replace with PPI sdk
            )

            if (error) {
                return undefined
            } else {
                const providers = result?.data?.data?.integrations.reduce(
                    (acc: TIntegrationProviders, integration: TIntegration) => {
                        if (acc[integration.id]) {
                            acc[integration.id].integrations.push(integration)
                        } else {
                            acc[integration.id] =
                                transformProviderObject(integration)
                        }
                        return acc
                    },
                    {} as Record<string, TIntegration>
                )
                return providers
            }
        }

        async newIntegration(
            providerId: string,
            account_id: string,
            value: string,
            prefix: string
        ): Promise<
            [(Error & AxiosError<unknown, any>) | undefined, string | undefined]
        > {
            const integrationValue = {
                [prefix]: value,
            } as
                | MicrosoftIntegrationData
                | GoogleIntegrationData
                | CrowdstrikeIntegrationData

            const [error, result] = await this.request(() =>
                this.client.addIntegration(account_id, providerId, {
                    value: integrationValue,
                    enable: true,
                })
            )
            if (error) {
                return [error, undefined]
            }
            return [undefined, result?.data?.data?.request_id]
        }

        async getIntegrationRequest(
            requestId: string
        ): Promise<
            [
                (Error & AxiosError<unknown, any>) | undefined,
                IntegrationRequestResponseData | undefined,
            ]
        > {
            const [error, result] = await this.request(
                () =>
                    this.client.getIntegrationRequest(
                        this.accountId,
                        requestId
                    ),
                {
                    poll: {
                        maxRetries: this.GET_INTEGRATION_MAX_RETRIES,
                        delay: this.INTERVAL,
                        shouldStop: (response) => {
                            return (
                                response?.data?.data?.status !==
                                IntegrationRequestResponseData.status.PENDING
                            )
                        },
                    },
                }
            )
            if (error) {
                return [error, undefined]
            }
            return [undefined, result?.data.data]
        }

        async getIntegrationsRequests(): Promise<
            [
                (Error & AxiosError<unknown, any>) | undefined,
                IntegrationsRequestsResponseData | undefined,
            ]
        > {
            const [error, result] = await this.request(
                async () => {
                    const response = await this.client.getIntegrationsRequests(
                        this.accountId,
                        [IntegrationRequestStatusEnum.PENDING]
                    )
                    return response.data
                },
                {
                    poll: {
                        maxRetries: this.GET_INTEGRATION_MAX_RETRIES,
                        delay: this.INTERVAL,
                        shouldStop: (
                            response: IntegrationsRequestsResponse | null
                        ) => {
                            const pendingProviderIds =
                                response?.data?.requests?.map(
                                    (request: IntegrationRequestResponseData) =>
                                        request.provider_id
                                ) || []

                            this.$eventBus.emit(
                                'pending_integrations_provider_ids',
                                {
                                    providerIds: pendingProviderIds,
                                }
                            )
                            return response?.data?.requests?.length === 0
                        },
                    },
                }
            )

            if (error) {
                return [error, undefined]
            }
            return [undefined, result?.data]
        }

        async deleteIntegration(
            providerId: string | undefined,
            integrationId: string | undefined
        ) {
            // TODO: replace with PPI sdk
            return await this.request(() =>
                this.http.delete(
                    `/api/accounts/${this.accountId}/providers/${providerId}/integration/${integrationId}`
                )
            )
        }

        async updateSecurityService(
            integrationId: string,
            serviceId: string,
            serviceName: string,
            enable: boolean
        ) {
            return this.request(() =>
                this.client.updateIntegrationSecurityServiceState(
                    this.accountId,
                    integrationId,
                    serviceId,
                    {
                        id: serviceId,
                        name: serviceName,
                        state: enable
                            ? SecurityServiceStateEnum.ENABLED
                            : SecurityServiceStateEnum.DISABLED,
                    }
                )
            )
        }

        async handleIntegrationLogin({
            providerId,
            type,
        }: {
            providerId: string
            type: string
        }) {
            try {
                switch (type) {
                    case 'microsoft':
                        const { MicrosoftIntegrationService } =
                            await import('@core/services/api-gateway/integrations-service/integrations/microsoft/login.service')
                        return MicrosoftIntegrationService.handleLogin(
                            providerId,
                            this.envVars.REACT_APP_MICROSOFT_APP_CLIENT_ID
                        )
                    case 'ms_fraud_defense':
                        const { MicrosoftIntegrationService: MsFraudDefense } =
                            await import('@core/services/api-gateway/integrations-service/integrations/microsoft/login.service')
                        return MsFraudDefense.handleLogin(
                            providerId,
                            this.envVars
                                .REACT_APP_MICROSOFT_FRAUD_DEFENSE_CLIENT_ID
                        )
                    case 'google':
                        const { GoogleIntegrationService } =
                            await import('@core/services/api-gateway/integrations-service/integrations/google/login.service')
                        return GoogleIntegrationService.handleLogin(providerId)
                    case 'google_fraud_defense':
                        const { GoogleIntegrationService: GgFraudDefense } =
                            await import('@core/services/api-gateway/integrations-service/integrations/google/login.service')
                        return GgFraudDefense.handleLogin(providerId)
                    default:
                        return
                }
            } catch (error) {
                return
            }
        }

        public async getIssues(delayScan = 0): Promise<TIssue[] | undefined> {
            if (delayScan > 0) {
                await sleep(delayScan)
            }
            const [error, result] = await this.request(() =>
                this.assetsClient.getAccountIssues(this.accountId)
            )

            if (error) {
                return []
            } else {
                return result?.data?.data?.issues?.map((issue) => ({
                    status: 'open',
                    ...issue,
                })) as TIssue[]
            }
        }

        private isJobFinished(status: string | undefined) {
            return status === 'completed' || status === 'failed'
        }

        public async pollIntegrationsScans(
            delayScan = 0
        ): Promise<TIssue[] | undefined> {
            let hadScdScans = false
            let scdEmitted = false
            const maxRetries = 15
            this.$eventBus.emit('security_configurations_scan_loading', {
                isLoading: true,
            })
            if (delayScan > 0) {
                await sleep(delayScan)
            }

            const [error] = await this.request(
                () => this.client.getAccountLatestJobs(this.accountId),
                {
                    poll: {
                        maxRetries,
                        delay: 3000,
                        shouldStop: async (response, retriesCompleted) => {
                            const integrations: any = response?.data?.data || []
                            const allJobs: any[] = integrations.flatMap(
                                (integration: any) => integration?.jobs ?? []
                            )
                            const securityConfigurationsJobs = allJobs.filter(
                                (job: any) =>
                                    job.job_type ===
                                    JobTypeEnum.PULL_SECURITY_CONFIGURATIONS_DATA
                            )

                            const hasSecurityConfigurationsScans =
                                securityConfigurationsJobs.some(
                                    (job: any) =>
                                        !this.isJobFinished(job.job_status)
                                )
                            const securityConfigurationsAllComplete =
                                securityConfigurationsJobs.length > 0 &&
                                securityConfigurationsJobs.every((job: any) =>
                                    this.isJobFinished(job.job_status)
                                )
                            const allComplete =
                                allJobs.length > 0 &&
                                allJobs.every((job: any) =>
                                    this.isJobFinished(job.job_status)
                                )

                            if (hasSecurityConfigurationsScans) {
                                hadScdScans = true
                                this.$eventBus.emit('scan_in_progress')
                            }
                            if (
                                securityConfigurationsAllComplete &&
                                hadScdScans &&
                                !scdEmitted
                            ) {
                                scdEmitted = true
                                this.$eventBus.emit('scan_finished')
                            }
                            return !!(integrations?.length === 0 || allComplete)
                        },
                    },
                }
            )

            if (error) {
                console.log('getLatestJobs error', error.message)
            }
            this.$eventBus.emit('security_configurations_scan_loading', {
                isLoading: false,
            })
            return
        }
    })()
